import React, { useState } from "react";
import { Mail, Instagram, Facebook, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Sparkle } from "../components/Decor2";
import { WavyBreak, FramedImage } from "../components/Wavy";
import { useSection } from "../context/SiteContext";

const Contact = () => {
  const { data: c } = useSection("contact");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Enquiry sent — we'll be in touch!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send enquiry");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-cream">
      <section className="relative pt-12 md:pt-20 pb-12 px-6 md:px-10 overflow-hidden">
        <Sparkle className="absolute top-16 left-14 w-6 h-6" color="#EC2675" />
        <Sparkle className="absolute top-28 right-24 w-5 h-5" color="#FFD23F" />

        <div className="max-w-[1100px] mx-auto grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7 text-center md:text-left">
            <span className="font-serif-display uppercase tracking-[0.3em] text-magenta text-[11px] md:text-sm font-semibold">THE STORY SO FAR</span>
            <h1 className="font-serif-display uppercase text-plum-deep text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]" data-testid="contact-about-title">
              {c.about_title || "ABOUT US"}
            </h1>
            <p className="mt-6 text-plum-deep/80 text-base md:text-lg max-w-xl mx-auto md:mx-0" data-testid="contact-about-body">
              {c.about_body}
            </p>
          </div>
          <div className="md:col-span-5 max-w-[340px] mx-auto w-full">
            <FramedImage src={c.image} alt="The Bloom Foundry studio" bg="#C39EE3" testid="contact-image" />
          </div>
        </div>
      </section>

      <WavyBreak color="#EC2675" />

      <section className="px-6 md:px-10 py-14 md:py-20 max-w-[1100px] mx-auto grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <h2 className="font-serif-display uppercase text-plum-deep text-3xl md:text-4xl">GET IN TOUCH</h2>
          <ul className="mt-8 space-y-5 text-plum-deep text-base">
            <li className="flex items-center gap-4" data-testid="contact-email">
              <span className="w-11 h-11 rounded-full bg-blush flex items-center justify-center shrink-0"><Mail className="w-4 h-4 text-magenta" /></span>
              {c.email || "hello@thebloomfoundry.nz"}
            </li>
            <li className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-blush flex items-center justify-center shrink-0"><Instagram className="w-4 h-4 text-magenta" /></span>
              {c.instagram || "@thebloomfoundry"}
            </li>
            <li className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-blush flex items-center justify-center shrink-0"><Facebook className="w-4 h-4 text-magenta" /></span>
              {c.facebook || "The Bloom Foundry"}
            </li>
            <li className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-blush flex items-center justify-center shrink-0"><MapPin className="w-4 h-4 text-magenta" /></span>
              {c.location || "New Zealand"}
            </li>
          </ul>
        </div>

        <div className="md:col-span-7">
          <form onSubmit={submit} className="bg-warmwhite border border-plum-deep/10 rounded-[2rem] p-6 md:p-10 space-y-4" data-testid="enquiry-form">
            <h3 className="font-serif-display uppercase text-plum-deep text-2xl tracking-[0.08em]">SEND AN ENQUIRY</h3>
            {sent && <p className="text-forest text-sm" data-testid="enquiry-success">Thanks! Your enquiry has been sent.</p>}
            <div className="grid sm:grid-cols-2 gap-4">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name *" data-testid="enquiry-name" className="w-full rounded-full px-6 py-3.5 bg-cream border border-plum-deep/15 focus:outline-none focus:border-magenta" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" data-testid="enquiry-email" className="w-full rounded-full px-6 py-3.5 bg-cream border border-plum-deep/15 focus:outline-none focus:border-magenta" />
            </div>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" data-testid="enquiry-subject" className="w-full rounded-full px-6 py-3.5 bg-cream border border-plum-deep/15 focus:outline-none focus:border-magenta" />
            <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us what you're dreaming up… *" data-testid="enquiry-message" className="w-full rounded-3xl px-6 py-4 bg-cream border border-plum-deep/15 focus:outline-none focus:border-magenta resize-none" />
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit"
              className="inline-flex items-center gap-3 rounded-full bg-magenta text-cream px-8 py-4 font-serif-display uppercase text-sm tracking-[0.18em] font-semibold hover:bg-plum transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> {sending ? "SENDING…" : "SEND ENQUIRY"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
